import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { ArrowLeft, Save } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ErrorState, LoadingState, Notice, PageHeader, StatusBadge } from "@/components/common/States";
import { api, errorMessage, fetcher } from "@/lib/api";
import { MEMBER_STATUS, ROLE_LABELS, TENANT_STATUS, valueLabel } from "@/lib/labels";

const Baris = ({ label, value }) => (
  <div className="flex justify-between gap-4 py-1.5">
    <span className="text-sm text-muted-foreground">{label}</span>
    <span className="text-sm font-medium text-right break-all">{value || "—"}</span>
  </div>
);

export default function MitraPelangganDetail() {
  const { id } = useParams();
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["mitra", "pelanggan", id], queryFn: () => fetcher(`/reseller/pelanggan/${id}`) });
  const [f, setF] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => { setF(null); setErr(""); }, [id]);

  if (q.isLoading) return <LoadingState rows={6} />;
  if (q.error) return <ErrorState error={q.error} onRetry={q.refetch} />;
  const d = q.data;
  const s = d.school;
  const form = f || { address: s.address || "", phone: s.phone || "", email: s.email || "" };
  const set = (k) => (e) => setF({ ...form, [k]: e.target.value });

  const simpan = async () => {
    setBusy(true);
    setErr("");
    try {
      const body = {};
      for (const k of ["address", "phone", "email"]) body[k] = form[k] === "" ? null : form[k];
      await api.patch(`/reseller/pelanggan/${id}`, body);
      await qc.invalidateQueries({ queryKey: ["mitra", "pelanggan"] });
      setF(null);
      toast.success("Kontak sekolah tersimpan.");
    } catch (ex) {
      setErr(errorMessage(ex));
    } finally {
      setBusy(false);
    }
  };

  const adaPerubahan = f && (form.address !== (s.address || "") || form.phone !== (s.phone || "") || form.email !== (s.email || ""));

  return (
    <div className="space-y-6">
      <div>
        <Button asChild variant="ghost" size="sm" className="mb-2 -ml-2" data-testid="mitra-detail-back">
          <Link to="/mitra/pelanggan"><ArrowLeft className="h-4 w-4 mr-1.5" /> Kelola Pelanggan</Link>
        </Button>
        <PageHeader eyebrow="Sekolah binaan" title={s.name}
          description={`${[s.city, s.level].filter(Boolean).join(" · ") || "—"} · terdaftar sejak ${new Date(s.created_at).toLocaleDateString("id-ID", { day: "numeric", month: "long", year: "numeric" })}`}
          actions={<StatusBadge map={TENANT_STATUS} value={s.status} testId="mitra-detail-status" />} />
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <section className="rounded-2xl border bg-card p-6 space-y-4" data-testid="mitra-detail-info">
          <div className="flex items-center justify-between gap-2">
            <h2 className="text-base md:text-lg font-bold">Info sekolah</h2>
            <StatusBadge map={TENANT_STATUS} value={s.status} />
          </div>
          <div className="divide-y">
            <Baris label="NPSN" value={s.npsn} />
            <Baris label="Jenjang" value={valueLabel(s.level)} />
            <Baris label="Kabupaten/Kota" value={s.city} />
            <Baris label="Kepala sekolah" value={s.principal_name} />
            <Baris label="Status" value={`${TENANT_STATUS[s.status]?.label || s.status} — ${s.status_reason || ""}`} />
          </div>
          <p className="text-xs text-muted-foreground">
            Nama, NPSN, jenjang, dan status sekolah diatur oleh Pusat Kendali SIST. Reseller hanya bisa melengkapi kontak di bawah.
          </p>

          <div className="space-y-3 border-t pt-4" data-testid="mitra-detail-contact-form">
            <div className="space-y-2">
              <Label htmlFor="mitra-detail-address">Alamat sekolah</Label>
              <Input id="mitra-detail-address" value={form.address} onChange={set("address")}
                placeholder="Jalan, nomor, kelurahan" data-testid="mitra-detail-address-input" />
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label htmlFor="mitra-detail-phone">Telepon</Label>
                <Input id="mitra-detail-phone" value={form.phone} onChange={set("phone")}
                  placeholder="08xxxxxxxxxx" data-testid="mitra-detail-phone-input" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="mitra-detail-email">Email sekolah</Label>
                <Input id="mitra-detail-email" type="email" value={form.email} onChange={set("email")}
                  placeholder="email@sekolah.id" data-testid="mitra-detail-email-input" />
              </div>
            </div>
            {err && <Notice tone="danger" testId="mitra-detail-error">{err}</Notice>}
            <Button onClick={simpan} disabled={busy || !adaPerubahan} data-testid="mitra-detail-save">
              {busy ? "Menyimpan…" : <><Save className="h-4 w-4 mr-1.5" /> Simpan kontak</>}
            </Button>
          </div>
        </section>

        <section className="rounded-2xl border bg-card p-6" data-testid="mitra-detail-anggota">
          <h2 className="text-base md:text-lg font-bold">Anggota sekolah</h2>
          <p className="text-xs text-muted-foreground mt-1">
            Hanya-baca: pengelolaan anggota dilakukan Admin Sekolah di ruang sekolahnya.
          </p>
          <div className="mt-3 flex flex-wrap gap-2" data-testid="mitra-detail-peran">
            {d.peran && Object.entries(d.peran).map(([role, n]) => (
              <span key={role} className="rounded-full border px-2.5 py-1 text-xs bg-muted/40">
                {ROLE_LABELS[role] || role}: <strong>{n}</strong>
              </span>
            ))}
            {d.status && (
              <span className="rounded-full border px-2.5 py-1 text-xs bg-muted/40">
                Aktif: <strong>{d.status.active}</strong> · Nonaktif: <strong>{d.status.inactive}</strong>
              </span>
            )}
            {typeof d.punya_akun === "number" && (
              <>
                <span className="rounded-full border px-2.5 py-1 text-xs bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300">
                  Punya akun: <strong>{d.punya_akun}</strong>
                </span>
                <span className="rounded-full border px-2.5 py-1 text-xs bg-amber-50 text-amber-700 dark:bg-amber-950/40 dark:text-amber-300">
                  Belum punya akun: <strong>{d.tanpa_akun}</strong>
                </span>
              </>
            )}
          </div>
          {d.anggota.length === 0 ? (
            <p className="text-sm text-muted-foreground mt-4 py-6 text-center">Belum ada anggota terdaftar.</p>
          ) : (
            <div className="mt-4 max-h-[26rem] overflow-y-auto rounded-xl border" data-testid="mitra-detail-anggota-list">
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-muted/60 backdrop-blur">
                  <tr className="text-left">
                    <th className="px-3 py-2 font-semibold">Nama</th>
                    <th className="px-3 py-2 font-semibold">Peran</th>
                    <th className="px-3 py-2 font-semibold">Akun</th>
                    <th className="px-3 py-2 font-semibold text-right">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {d.anggota.map((a) => (
                    <tr key={a.membership_id} data-testid={`mitra-detail-member-${a.membership_id}`}>
                      <td className="px-3 py-2 font-medium">{a.name}</td>
                      <td className="px-3 py-2 text-muted-foreground">{ROLE_LABELS[a.role] || a.role}</td>
                      <td className="px-3 py-2 text-xs text-muted-foreground break-all">
                        {a.email || "Belum punya akun"}
                      </td>
                      <td className="px-3 py-2 text-right"><StatusBadge map={MEMBER_STATUS} value={a.status} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
